import { useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  MarkerLabelFieldTypeEnum,
  PointOfInterest,
  PointOfInterestTemplate,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  UnitsEnum,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import {
  BLANK_SPACE,
  NOT_APPLICABLE_LABEL,
  SC_CATEGORY_KEY,
  SUMMARY_VALUE_ICONS,
} from '@/constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { useBlisterFieldConfig } from '@/hooks/useBlisterFieldConfig';
import { formatFieldValue } from '@/utils';
import { getReviewPanelFieldValue } from './getReviewPanelFieldValue';

interface ReviewPanelField {
  key: string;
  type: MarkerLabelFieldTypeEnum;
}

const BLISTER_PIT_DEFAULT_FIELDS: ReviewPanelField[] = [
  { key: 'maxHeight', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'pdrHeight', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'residualCorrosionAllowance', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'wallLoss', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'measurementConfidence', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'distanceConfidence', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'deckRelativeHeight', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'paintRegion', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'deckName', type: MarkerLabelFieldTypeEnum.PointOfInterest },
];

const POI_DEFAULT_FIELDS: ReviewPanelField[] = [
  { key: 'deckRelativeHeight', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'paintRegion', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'deckName', type: MarkerLabelFieldTypeEnum.PointOfInterest },
];

export const usePoiTemplateFields = ({ data }: { data: PointOfInterest }) => {
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const blisterFieldConfig = useBlisterFieldConfig({ type: data.type });

  const { data: allPointOfInterestTemplates } = useFetchTemplatesByType();

  const isAutoPOI = data.isAutoGenerated || !!data.blister;

  const templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined = useMemo(() => {
    return allPointOfInterestTemplates?.allPointOfInterestTemplate.find(
      ({ id, key }) =>
        id === data.template?.id ||
        id === data.templateId ||
        (isAutoPOI && (key === 'blister' || key === 'pit'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPointOfInterestTemplates, data]);

  const getTemplateFieldValue = useCallback(
    (field: { id?: string; key?: string | null }) =>
      (field?.key ? data.templateFieldData?.[field.key] : undefined) ??
      (field?.id
        ? data.templateData?.find((templateData) => templateData.id === field.id)?.value
        : undefined) ??
      NOT_APPLICABLE_LABEL,
    [data.templateData, data.templateFieldData]
  );

  const getFormattedTemplateFieldValue = useCallback(
    (
      value: unknown,
      field: {
        type?: TemplateFieldTypeEnum | null;
        unit?: unknown;
        displayUnit?: unknown;
        roundToDecimal?: number | null;
      }
    ) => {
      if (value === NOT_APPLICABLE_LABEL) return NOT_APPLICABLE_LABEL;
      if (typeof value !== 'string' && typeof value !== 'number') return String(value);
      if (typeof value === 'string' && value.trim().length === 0) return value;

      const sourceUnit = Object.values(UnitsEnum).includes(field.unit as UnitsEnum)
        ? (field.unit as UnitsEnum)
        : undefined;
      const displayAsUnit =
        field.displayUnit && Object.values(UnitsEnum).includes(field.displayUnit as UnitsEnum)
          ? (field.displayUnit as UnitsEnum)
          : undefined;

      return formatFieldValue({
        unitSystem: unitConversionSystem,
        type: field.type ?? undefined,
        unit: sourceUnit,
        displayUnit: displayAsUnit,
        value: String(value),
        roundToDecimal: field.roundToDecimal ?? undefined,
        fallbackLabel: NOT_APPLICABLE_LABEL,
      });
    },
    [unitConversionSystem]
  );

  const getTemplateFieldFallbackValue = useCallback(
    (
      field: {
        key?: string | null;
        name?: string | null;
        type?: TemplateFieldTypeEnum | null;
      },
      value: unknown
    ) => {
      let resolvedValue = value;
      const normalizedFieldName = (field.name ?? '').trim().toLowerCase();
      const fallbackDeckFromTemplateFieldData = (() => {
        const entries = Object.entries(data.templateFieldData ?? {});
        const matched = entries.find(([key, val]) => key.toLowerCase().includes('deck') && !!val);
        return matched?.[1];
      })();

      if (
        (resolvedValue === NOT_APPLICABLE_LABEL ||
          resolvedValue === undefined ||
          resolvedValue === null) &&
        normalizedFieldName === 'paint block'
      ) {
        resolvedValue = data.paintRegion?.name ?? NOT_APPLICABLE_LABEL;
      }

      if (
        (resolvedValue === NOT_APPLICABLE_LABEL ||
          resolvedValue === undefined ||
          resolvedValue === null) &&
        (normalizedFieldName === 'deck' || normalizedFieldName === 'deck name')
      ) {
        resolvedValue =
          data.paintRegion?.deck?.name ?? fallbackDeckFromTemplateFieldData ?? NOT_APPLICABLE_LABEL;
      }

      if (
        field.key === SC_CATEGORY_KEY &&
        (!resolvedValue || resolvedValue === NOT_APPLICABLE_LABEL)
      ) {
        resolvedValue = data.blister?.scCategory ?? NOT_APPLICABLE_LABEL;
      }

      if (field.type === TemplateFieldTypeEnum.MeasurementPaintLength) {
        const points = data.lineMeasurement?.points;
        if (Array.isArray(points) && points.length < 2) {
          resolvedValue = '';
        }
      }

      if (
        field.type === TemplateFieldTypeEnum.MeasurementPaintDiameter &&
        (!resolvedValue || resolvedValue === NOT_APPLICABLE_LABEL || resolvedValue === BLANK_SPACE)
      ) {
        resolvedValue = data.assembly?.pipeSpec?.size ?? NOT_APPLICABLE_LABEL;
      }

      if (
        field.type === TemplateFieldTypeEnum.MeasurementPaintArea &&
        (resolvedValue === BLANK_SPACE || resolvedValue === undefined || resolvedValue === null)
      ) {
        resolvedValue = NOT_APPLICABLE_LABEL;
      }

      return resolvedValue;
    },
    [
      data.assembly?.pipeSpec?.size,
      data.blister?.scCategory,
      data.lineMeasurement?.points,
      data.paintRegion?.deck?.name,
      data.paintRegion?.name,
      data.templateFieldData,
    ]
  );

  const resolveConfigFields = useCallback(
    (
      fields: ReviewPanelField[],
      fieldConfig?: typeof blisterFieldConfig
    ): Array<{
      label: string;
      value: string;
      type?: TemplateFieldTypeEnum;
      valueIcon?: string;
      displayStyle?: TableCellStyleEnum;
    }> => {
      return fields.flatMap((field) => {
        // Blister & POI — delegate to dynamic resolver
        if (
          field.type === MarkerLabelFieldTypeEnum.Blister ||
          field.type === MarkerLabelFieldTypeEnum.PointOfInterest
        ) {
          const resolved = getReviewPanelFieldValue(field, data, unitConversionSystem, fieldConfig);
          return resolved ? [resolved] : [];
        }

        // TEMPLATE_FIELD — keep existing enrichment logic
        const templateField = templateOfSelectedPoi?.fields?.find((f) => f.key === field.key);
        if (!templateField) return [];
        const rawValue = getTemplateFieldValue(templateField);
        const resolvedValue = getTemplateFieldFallbackValue(templateField, rawValue);
        const value = getFormattedTemplateFieldValue(resolvedValue, templateField);
        const matchingIcon = SUMMARY_VALUE_ICONS.find(
          (iconConfig) => iconConfig.key === templateField.key && iconConfig.value === value
        );
        return [
          {
            value,
            label: templateField.name || NOT_APPLICABLE_LABEL,
            type: templateField.type,
            displayStyle: templateField.displayConfig?.type,
            ...(matchingIcon && { valueIcon: matchingIcon.icon }),
          },
        ];
      });
    },
    [
      data,
      unitConversionSystem,
      templateOfSelectedPoi,
      getTemplateFieldValue,
      getTemplateFieldFallbackValue,
      getFormattedTemplateFieldValue,
    ]
  );

  const isBlisterOrPit = useMemo(
    () => templateOfSelectedPoi?.key === 'blister' || templateOfSelectedPoi?.key === 'pit',
    [templateOfSelectedPoi]
  );

  const templateFieldsWithValues: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
    valueIcon?: string;
    displayStyle?: TableCellStyleEnum | undefined;
  }> = useMemo(() => {
    const configFields = templateOfSelectedPoi?.poiReviewPanelConfig?.fields as
      | ReviewPanelField[]
      | undefined;
    const hasConfig = configFields && configFields.length > 0;

    if (hasConfig) {
      return resolveConfigFields(configFields);
    }

    // Default: type-aware fallback
    const defaultFields = isBlisterOrPit ? BLISTER_PIT_DEFAULT_FIELDS : POI_DEFAULT_FIELDS;
    return resolveConfigFields(defaultFields);
  }, [templateOfSelectedPoi?.poiReviewPanelConfig, isBlisterOrPit, resolveConfigFields]);

  return {
    templateFieldsWithValues,
    templateOfSelectedPoi,
  };
};
